/**
 * Chat API — text-mode interactions against the LiveKit-agent REST.
 *
 *   POST /chat/v1/{sessionId}/message
 *
 * Two transports, one endpoint, picked by Accept header:
 *   - Accept: application/json   → blocking JSON ChatResponse (default)
 *   - Accept: text/event-stream  → SSE: many `delta` events, one `done`
 *
 * The JSON path uses the shared axios client (auth interceptors, logging,
 * 401 refresh-and-retry). The SSE path uses fetch directly because axios
 * doesn't expose a streaming body. Both are exported as standalone
 * functions so RN, the embed widget, and wunderguide-web can pick which
 * fits their UI.
 */
import type { AxiosInstance } from 'axios';
import type { ChatContentItem } from '@wg/types';

export type { ChatContentItem };

export interface ChatHistoryMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/**
 * Wire-shape of a single content item. `ChatMessageItem` stays as the
 * permissive over-the-wire type (anything goes — the backend can ship a
 * field we haven't typed yet); `parseContentItem` narrows it to the
 * discriminated `ChatContentItem` for client-side rendering.
 */
export interface ChatMessageItem {
  type: string;
  text?: string;
  data?: unknown;
  render_hint?: string;
  [k: string]: unknown;
}

export interface ChatResponseMeta {
  /** Stable id of the interactive prompt the user is responding to. */
  in_response_to?: string;
  /** The structured value of the chosen option (chip.value, form payload). */
  structured_value?: unknown;
  /** Catch-all for partner-specific tags. */
  [k: string]: unknown;
}

export interface SendChatMessageOptions {
  history?: ChatHistoryMessage[];
  language?: string;
  signal?: AbortSignal;
  includeContent?: boolean;
  /**
   * IANA timezone (e.g. "Pacific/Honolulu"). When omitted the shared client
   * derives it from the runtime — `Intl.DateTimeFormat().resolvedOptions()`
   * in browsers, RN passes through `getTimeZone()`. Server uses this as a
   * fallback when session_ctx.timeZone isn't already set (i.e. text-only
   * chats that never went through /sessions/{sid}/start).
   *
   * Pass `null` to omit the field entirely (no device resolution). The
   * embed widget does this — its wire shape historically never carried
   * timeZone, and the server keeps the session_ctx timezone established
   * by the voice path instead.
   */
  timeZone?: string | null;
  /**
   * Per-message metadata. Most common: `{in_response_to, structured_value}`
   * for responses to interactive prompts (chips, forms). Persisted on
   * agent_interactions.metadata.
   */
  metadata?: ChatResponseMeta;
  /**
   * Optional inline still image attached to this turn
   * ({ data: base64, mime?: 'image/jpeg', url?: storage_url }). Enables vision
   * tools (analyze_menu_image / analyze_scene). Honored server-side only when a
   * vision feature flag (dining_concierge / live_vision) is enabled.
   */
  image?: { data: string; mime?: string; url?: string };
}

export interface SendChatMessageResponse {
  response: string;
  content?: ChatMessageItem[];
  interaction_id?: string | null;
  intent?: string | null;
  confidence?: number | null;
  latency_ms?: number;
  tools_used?: string[];
}

/**
 * Best-effort device timezone resolution. Browsers expose `Intl`; RN does too
 * (Hermes ships full Intl since RN 0.72), but RN callers typically pass an
 * explicit value via `getTimeZone()` from react-native-localize so this is
 * just a safety net. Returns undefined if the runtime can't tell us — server
 * has its own UTC fallback.
 */
function resolveDeviceTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === 'string' && tz ? tz : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Blocking JSON request — works the same as the inlined chatApi.ts in each
 * webapp today. Existing RN/web/embed callers can switch to this without
 * behavior change.
 */
export async function sendChatMessage(
  http: AxiosInstance,
  sessionId: string,
  message: string,
  options: SendChatMessageOptions = {},
): Promise<SendChatMessageResponse> {
  const timeZone =
    options.timeZone === null
      ? undefined
      : options.timeZone || resolveDeviceTimeZone();
  const { data } = await http.post<SendChatMessageResponse>(
    `/chat/v1/${sessionId}/message`,
    {
      message,
      history: options.history,
      language: options.language || 'en-US',
      timeZone,
      metadata: options.metadata,
      include_content: options.includeContent !== false,
      ...(options.image ? { image: options.image } : {}),
    },
    { timeout: 60000, signal: options.signal },
  );
  return data;
}

/**
 * Deps for the streaming path. We can't reuse axios's interceptors here, so
 * the caller passes baseURL + auth-token getter. Keep these aligned with
 * what `createHttpClient` already receives.
 */
export interface StreamChatMessageDeps {
  baseURL: string;
  getAuthToken: () => Promise<string | null> | string | null;
  getSelectedLanguage?: () => string;
}

export interface StreamChatHandlers {
  /** One per text delta from the model. */
  onDelta(text: string): void;
  /**
   * One per atomic content item emitted mid-stream (cards, quick_replies,
   * forms, …). `position` is the item's index in the assistant's final
   * `content` array; renderers use it to insert the card at a stable spot
   * even when the agent's running text continues to stream after.
   *
   * Optional: clients that don't care about progressive content can pull
   * the full list out of `onDone`'s payload — `done` is still the
   * source-of-truth.
   */
  onContent?(item: ChatContentItem, position: number): void;
  /** Final state including interaction_id, content cards, intent, tools. */
  onDone(payload: SendChatMessageResponse): void;
  /** Stream-level error (network, server-emitted `error` SSE event, parse). */
  onError?(err: Error): void;
}

/**
 * Narrow a wire-shaped `ChatMessageItem` (open `type: string`) to a typed
 * `ChatContentItem`. Unknown `type`s land in `UnknownContentItem` so the
 * caller can render a graceful fallback rather than crashing on a backend
 * that ships ahead of the client.
 *
 * This intentionally trusts the wire shape — fields are spread through. We
 * don't deeply validate; if the backend says `{type: "card", card_type:
 * "activity"}` we trust the rest is roughly card-shaped. The types here
 * are mostly editor-time guidance; runtime safety comes from each renderer
 * coping with missing optional fields.
 */
export function parseContentItem(raw: unknown): ChatContentItem {
  if (!raw || typeof raw !== 'object') {
    return { type: 'unknown', raw: { value: raw } as Record<string, unknown> };
  }
  const obj = raw as Record<string, unknown>;
  const type = typeof obj.type === 'string' ? obj.type : 'unknown';

  // Known kinds — pass through with the original fields. Renderers consume
  // these via the discriminated union; missing optional fields are their
  // problem to handle.
  switch (type) {
    case 'text':
    case 'markdown':
    case 'html':
    case 'image':
    case 'card':
    case 'carousel':
    case 'map':
    case 'quick_replies':
    case 'multi_select':
    case 'form':
    case 'confirm':
    case 'actions':
    case 'app_action':
    case 'page_action':
    case 'availability':
    case 'loading':
      return obj as unknown as ChatContentItem;
    default:
      return { type, raw: obj };
  }
}

/**
 * Open an SSE stream for a chat message. Resolves when the stream finishes
 * (either after `done` or `error`). Reject only on transport-level failures
 * before the stream starts — server-side errors come through `onError`.
 */
export async function streamChatMessage(
  deps: StreamChatMessageDeps,
  sessionId: string,
  message: string,
  handlers: StreamChatHandlers,
  options: SendChatMessageOptions = {},
): Promise<void> {
  const token = await deps.getAuthToken();
  if (!token) {
    throw new Error('streamChatMessage: missing auth token');
  }

  const language = options.language || deps.getSelectedLanguage?.() || 'en-US';
  const url = `${deps.baseURL.replace(/\/+$/, '')}/chat/v1/${encodeURIComponent(sessionId)}/message`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Authorization: `Bearer ${token}`,
    'Accept-Language': language,
  };

  const timeZone =
    options.timeZone === null
      ? undefined
      : options.timeZone || resolveDeviceTimeZone();

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message,
        history: options.history,
        language,
        timeZone,
        metadata: options.metadata,
        include_content: options.includeContent !== false,
      }),
      signal: options.signal,
    });
  } catch (err) {
    handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!res.ok) {
    handlers.onError?.(
      new Error(
        `Chat stream failed: ${res.status} ${res.statusText || ''}`.trim(),
      ),
    );
    return;
  }

  // React Native's fetch doesn't expose `res.body` as a streaming
  // ReadableStream (would require react-native-polyfill-globals or
  // react-native-fetch-api). Falling back to a full-buffer read lets
  // RN clients still process the assistant's reply — they just lose
  // live token-by-token deltas. The final text + content items still
  // arrive via the SSE `done` frame which we parse below.
  if (!res.body) {
    try {
      const fullText = await res.text();
      let buf = fullText;
      let boundary = buf.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buf.slice(0, boundary);
        buf = buf.slice(boundary + 2);
        boundary = buf.indexOf('\n\n');
        const event = parseSseFrame(frame);
        if (event) dispatchSseEvent(event, handlers);
      }
    } catch (err) {
      handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
    }
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line. Process all complete
      // frames in the buffer; keep the trailing partial for the next read.
      let boundary = buf.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buf.slice(0, boundary);
        buf = buf.slice(boundary + 2);
        boundary = buf.indexOf('\n\n');

        const event = parseSseFrame(frame);
        if (!event) continue;

        const isDataEvent =
          event.event === 'delta' || event.event === 'content';
        dispatchSseEvent(event, handlers);
        // Yield to a macrotask after every data event so React can
        // commit the setMessages each delta triggers before we hand it
        // the next one. setTimeout(0) (not rAF) so it works reliably in
        // iframes where rAF can be throttled when the iframe loses focus.
        if (isDataEvent) await microYield();
      }
    }
  } catch (err) {
    handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* already released */
    }
  }
}

function dispatchSseEvent(
  event: { event: string; data: unknown },
  handlers: StreamChatHandlers,
): void {
  if (event.event === 'delta') {
    const text = (event.data as { text?: string } | undefined)?.text;
    if (text) handlers.onDelta(text);
  } else if (event.event === 'content') {
    // Mid-stream content item (atomic — backend emits when fully
    // assembled). The full list still arrives in `done`; this is an
    // optimization so cards can pop in as their tools resolve rather
    // than waiting for the assistant's whole response.
    const data = event.data as
      | { item?: unknown; position?: number }
      | undefined;
    if (data?.item) {
      const item = parseContentItem(data.item);
      const position = typeof data.position === 'number' ? data.position : 0;
      handlers.onContent?.(item, position);
    }
  } else if (event.event === 'done') {
    handlers.onDone(event.data as SendChatMessageResponse);
  } else if (event.event === 'error') {
    const detail =
      (event.data as { detail?: string } | undefined)?.detail || 'stream error';
    handlers.onError?.(new Error(detail));
  }
}

// Yield to a macrotask so React can commit pending setMessages calls
// between deltas. setTimeout(0) (clamped to ~4ms) is preferred over rAF
// because rAF gets throttled in iframes that lose focus, which would
// stall the SSE pump and make streaming look like a single late paint.
function microYield(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

function parseSseFrame(frame: string): { event: string; data: unknown } | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (!line) continue;
    if (line.startsWith(':')) continue; // SSE comment
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const valueRaw = colon === -1 ? '' : line.slice(colon + 1);
    const value = valueRaw.startsWith(' ') ? valueRaw.slice(1) : valueRaw;
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (dataLines.length === 0) return null;

  const dataStr = dataLines.join('\n');
  let data: unknown = dataStr;
  try {
    data = JSON.parse(dataStr);
  } catch {
    /* keep as raw string */
  }
  return { event, data };
}

// ============================================================================
// Chat history
// ============================================================================

/**
 * Client-side message shape both web apps render from. Each row carries the
 * full `content` item list (text + cards + forms …) for one turn.
 */
export interface ChatHistoryItem {
  id: string;
  session_id: string;
  role: 'user' | 'assistant' | 'system';
  content: ChatMessageItem[];
  created_at: string;
}

/**
 * Plain text-chat thread — GET /chat/v1/{sessionId}/messages. Rows come back
 * already in the ChatHistoryItem shape; no client-side reshaping.
 */
export async function getChatMessages(
  http: AxiosInstance,
  sessionId: string,
  limit = 30,
  offset = 0,
): Promise<{
  messages: ChatHistoryItem[];
  hasMore: boolean;
  totalCount: number;
}> {
  const { data } = await http.get(`/chat/v1/${sessionId}/messages`, {
    params: { limit, offset },
  });
  return {
    messages: data.messages ?? [],
    hasMore: !!data.has_more,
    totalCount: data.total_count ?? data.totalCount ?? 0,
  };
}

/**
 * Merged voice + text history via the legacy edge function —
 * GET /functions/v1/chat?sessionId=… . Mirrors the RN client's
 * `useMessages.fetchMessages`. The endpoint returns DESC order with a
 * `transcript` field that may be empty for non-textual events; we filter
 * empties and reverse to ASC for display. Used by wunderguide-web.
 *
 * Note: transcript strings only — persisted content items (cards, forms)
 * are NOT included. Clients that need them should use
 * `getInteractionHistory` instead.
 */
export async function getLegacyMergedHistory(
  http: AxiosInstance,
  sessionId: string,
  limit = 50,
  offset = 0,
): Promise<{ messages: ChatHistoryItem[]; hasMore: boolean }> {
  const { data } = await http.get('/functions/v1/chat', {
    params: { sessionId, limit, offset },
  });
  const raw: Array<{
    id: string;
    role: 'user' | 'assistant';
    transcript?: string;
    start_time: string;
    mode?: string;
    message_type?: string;
  }> = data?.messages ?? [];
  const messages: ChatHistoryItem[] = raw
    .filter(m => typeof m.transcript === 'string' && m.transcript.trim() !== '')
    .map(m => ({
      id: m.id,
      session_id: sessionId,
      role: m.role,
      content: [{ type: 'text', text: m.transcript ?? '' }],
      created_at: m.start_time,
    }))
    .reverse();
  return {
    messages,
    hasMore: messages.length >= limit,
  };
}

/**
 * Merged history via the FastAPI endpoint — GET /chat/v1/{sessionId}/history.
 * Joins interaction_content so persisted content items (place cards,
 * quick-replies, forms, …) survive a hard refresh; the legacy edge endpoint
 * only ships transcript strings. Returns oldest-first already; no reverse()
 * needed. Used by the embed widget.
 */
export async function getInteractionHistory(
  http: AxiosInstance,
  sessionId: string,
  limit = 50,
  offset = 0,
): Promise<{ messages: ChatHistoryItem[]; hasMore: boolean }> {
  const { data } = await http.get<{
    messages: Array<{
      role: 'user' | 'assistant' | 'system';
      content: string;
      timestamp?: string | null;
      interaction_id?: string | null;
      content_items?: Array<Record<string, unknown>>;
    }>;
    has_more: boolean;
  }>(`/chat/v1/${encodeURIComponent(sessionId)}/history`, {
    params: { limit, offset },
  });
  const raw = data?.messages ?? [];
  const messages: ChatHistoryItem[] = raw
    .filter(m => {
      const hasText = typeof m.content === 'string' && m.content.trim() !== '';
      const hasItems = (m.content_items?.length ?? 0) > 0;
      // Drop the rare row that has neither — historically those were
      // empty user messages that slipped past validation.
      return hasText || hasItems;
    })
    .map(m => {
      const items: ChatMessageItem[] = [];
      if (m.content && m.content.trim()) {
        items.push({ type: 'text', text: m.content } as ChatMessageItem);
      }
      for (const ci of m.content_items ?? []) {
        items.push(ci as unknown as ChatMessageItem);
      }
      return {
        id: m.interaction_id ?? `${sessionId}-${m.timestamp ?? Math.random()}`,
        session_id: sessionId,
        role: (m.role === 'system' ? 'assistant' : m.role) as
          | 'user'
          | 'assistant',
        content: items,
        created_at: m.timestamp ?? new Date().toISOString(),
      };
    });
  return { messages, hasMore: data?.has_more ?? false };
}

// ============================================================================
// Guide catalog
// ============================================================================

/**
 * A selectable travel guide from the DB-driven catalog
 * (GET /functions/v1/chat/guides). Every active agent with active coverage:
 * location guides (`kind: 'geo'`, facet 'general') and experience overlays
 * (`kind: 'overlay'`, e.g. facet 'lgbtq' / 'black_heritage'). New geos added
 * to the backend auto-appear with no client release.
 */
export interface Guide {
  agent_id: string;
  name: string;
  locale?: string | null;
  description?: string | null;
  avatar_image_url?: string | null;
  default_language?: string | null;
  kind: 'geo' | 'overlay';
  facet: string;
}

/**
 * Fetch the guide catalog. Additive/optional: callers degrade to their
 * existing sessions + search flow on any error (older backend, network), so
 * this never throws — it returns an empty list instead.
 */
export async function getGuides(http: AxiosInstance): Promise<Guide[]> {
  try {
    const { data } = await http.get<{ guides?: Guide[] }>(
      `/functions/v1/chat/guides`,
    );
    return Array.isArray(data?.guides) ? data.guides : [];
  } catch {
    return [];
  }
}

// ============================================================================
// Session bootstrap
// ============================================================================

const DEFAULT_DESTINATION_SEARCH = 'Hawaii';

export interface BootstrapResult {
  id: string;
  agent?: { id?: string; displayName?: string; avatarUrl?: string };
}

export interface BootstrapOptions {
  /**
   * Pinned agent UUID from partner_embedded_instances.agent_id. When set,
   * bootstrap binds new sessions to this agent and prefers existing
   * sessions whose agent matches. When null, falls back to the
   * location-default agent (legacy WunderGuide-web behavior).
   */
  pinnedAgentId?: string | null;
  /**
   * Instance-level presentation overrides from
   * partner_embedded_instances.persona. These overlay the agent's default
   * name + avatar in the chat header. Empty strings are treated as unset.
   */
  displayOverrides?: {
    displayName?: string | null;
    avatarUrl?: string | null;
  };
  /**
   * Location query used to resolve the default agent when no pin is set.
   * Defaults to 'Hawaii'.
   */
  defaultDestinationSearch?: string;
}

function applyDisplayOverrides(
  agent: BootstrapResult['agent'],
  overrides?: BootstrapOptions['displayOverrides'],
): BootstrapResult['agent'] {
  if (!agent || !overrides) return agent;
  const displayName = overrides.displayName?.trim() || agent.displayName;
  const avatarUrl = overrides.avatarUrl?.trim() || agent.avatarUrl;
  return { ...agent, displayName, avatarUrl };
}

/**
 * Find or create a chat session for the current user. One implementation
 * for both wunderguide-web (no options — most-recent session, location
 * default agent) and the embed widget (instance-pinned agent + persona
 * display overrides).
 *
 * When `pinnedAgentId` is provided (embed instance has a configured agent),
 * we prefer reusing an existing session bound to that agent and create new
 * sessions against it — bypassing the location-default lookup. This keeps
 * `agent_sessions.agent_id` and the chat-header display aligned with the
 * partner-pinned agent (e.g. "Leilani (Partner)" instead of the generic
 * "Leilani"). Runtime persona resolution in livekit-agent already prefers
 * this pin server-side, so the fix is purely about display correctness.
 */
export async function bootstrapChatSession(
  http: AxiosInstance,
  opts: BootstrapOptions = {},
): Promise<BootstrapResult | null> {
  const { pinnedAgentId, displayOverrides } = opts;
  try {
    const list = await http.get<{
      sessions?: Array<{
        id: string;
        agent_id?: string;
        agent_display_name?: string;
        agent_avatar_url?: string;
      }>;
    }>(`/functions/v1/chat/sessions`);
    const arr = Array.isArray(list.data)
      ? (list.data as Array<{
          id: string;
          agent_id?: string;
          agent_display_name?: string;
          agent_avatar_url?: string;
        }>)
      : (list.data?.sessions ?? []);
    if (arr.length > 0) {
      // With a pinned agent: ONLY reuse a session bound to that agent. If
      // none match, fall through to create a fresh session with the pin —
      // do NOT resurrect arr[0], which would be a session bound to the
      // wrong (location-default) agent created before the pin was set.
      // Without a pin: legacy behavior, take the most-recent session.
      const s = pinnedAgentId
        ? arr.find(x => x.agent_id === pinnedAgentId) || null
        : arr[0]?.id
          ? arr[0]
          : null;
      if (s?.id) {
        return {
          id: s.id,
          agent: applyDisplayOverrides(
            {
              id: s.agent_id,
              displayName: s.agent_display_name,
              avatarUrl: s.agent_avatar_url,
            },
            displayOverrides,
          ),
        };
      }
    }
  } catch {
    /* fall through to create */
  }
  try {
    // Resolve the agent to create against. Instance pin wins; otherwise
    // we fall back to the location-default agent from /locations.
    let agentId: string | undefined = pinnedAgentId || undefined;
    let fallbackDisplayName: string | undefined;
    let fallbackAvatarUrl: string | undefined;
    if (!agentId) {
      const loc = await http.get<{
        agents?: Array<{
          id?: string;
          display_name?: string;
          name?: string;
          avatar_url?: string;
        }>;
      }>(`/functions/v1/locations`, {
        params: {
          search: opts.defaultDestinationSearch || DEFAULT_DESTINATION_SEARCH,
        },
      });
      const agent = loc.data?.agents?.[0];
      if (!agent?.id) return null;
      agentId = agent.id;
      fallbackDisplayName = agent.display_name ?? agent.name;
      fallbackAvatarUrl = agent.avatar_url;
    }
    const created = await http.post<{
      session?: {
        id?: string;
        agent_id?: string;
        agent_display_name?: string;
        agent_avatar_url?: string;
      };
    }>(`/functions/v1/chat/sessions`, {
      title: 'Chat',
      agentId,
    });
    const session =
      created.data?.session ??
      (created.data as unknown as {
        id?: string;
        agent_id?: string;
        agent_display_name?: string;
        agent_avatar_url?: string;
      });
    if (!session?.id) return null;
    return {
      id: session.id,
      agent: applyDisplayOverrides(
        {
          id: session.agent_id ?? agentId,
          displayName: session.agent_display_name ?? fallbackDisplayName,
          avatarUrl: session.agent_avatar_url ?? fallbackAvatarUrl,
        },
        displayOverrides,
      ),
    };
  } catch {
    return null;
  }
}
